import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'
import { type HttpResult } from '@owl-nest/api-client/next'

const PROJECT_NEWS_CACHE = jotai.atom<hooks.Cache<api.NewsListPage>>({})

export type UseProjectNewsParams = {
  limit?: number
  page?: number
  q?: string
  sort?: string
  status?: string
}

type UseProjectNewsConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseProjectNewsParams
  withToken?: boolean
}

export function toURLSearchParams({ limit, page, sort, status }: UseProjectNewsParams): URLSearchParams {
  const searchParams = new URLSearchParams()
  if (limit !== undefined) {
    searchParams.set('limit', String(limit))
  }
  if (page !== undefined) {
    searchParams.set('offset', String((page - 1) * (limit ?? 20)))
  }
  if (status !== undefined) {
    searchParams.set('status', String(status))
  }
  if (sort !== undefined) {
    searchParams.set('sort', String(sort))
  }
  return searchParams
}

export type UseProjectNews = {
  response: hooks.Response<api.NewsListPage>
  query: () => Promise<void>
  createDraft: (payload: api.CreateNewsDraft) => Promise<HttpResult<api.News> | undefined>
  publishDraft: (newsId: number) => Promise<HttpResult<api.News> | undefined>
  unpublishPublication: (newsId: number) => Promise<HttpResult<api.News> | undefined>
  deletePublication: (newsId: number) => Promise<HttpResult<void> | undefined>
  updatePublication: (newsId: number, payload: api.CreateNewsDraft) => Promise<HttpResult<api.News> | undefined>
}

export function useProjectNews(
  projectSlugOrId?: number | string,
  { fetchOnMount = true, params = {}, withToken = false }: UseProjectNewsConfig = {},
): UseProjectNews {
  const setProjectNewsCache = jotai.useSetAtom(PROJECT_NEWS_CACHE)

  const sharedQuery = hooks.useSharedQuery(
    async (projectId: number | string, params: UseProjectNewsParams, withToken: boolean) =>
      base.getPage(projectId, toURLSearchParams(params), withToken),
    {
      atom: PROJECT_NEWS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(projectSlugOrId, params, withToken))

  return {
    response: getResponse(),
    query,
    createDraft,
    publishDraft,
    unpublishPublication,
    updatePublication,
    deletePublication,
  }

  function getResponse(): hooks.Response<api.NewsListPage> {
    if (projectSlugOrId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([projectSlugOrId, params, withToken])
  }

  async function query(): Promise<void> {
    if (projectSlugOrId === undefined) {
      return
    }

    return sharedQuery.query(projectSlugOrId, params, withToken)
  }

  async function createDraft(payload: api.CreateNewsDraft): Promise<HttpResult<api.News> | undefined> {
    if (projectSlugOrId === undefined) {
      return
    }

    const createDraftResponse = await base.createDraft(projectSlugOrId, payload)

    createDraftResponse.next(() => {
      const newCache = Object.fromEntries(
        Object.entries(sharedQuery.cache).filter(([key]) => {
          return !key.startsWith(String(projectSlugOrId))
        }),
      )

      setProjectNewsCache(newCache)
      query()
    })

    return createDraftResponse
  }

  async function publishDraft(newsId: number): Promise<HttpResult<api.News> | undefined> {
    if (projectSlugOrId === undefined) {
      return
    }

    const publishDraftResponse = await base.publishDraft(newsId)

    publishDraftResponse.next(() => {
      const newCache = Object.fromEntries(
        Object.entries(sharedQuery.cache).filter(([key]) => {
          return !key.startsWith(String(projectSlugOrId))
        }),
      )

      setProjectNewsCache(newCache)
      query()
    })

    return publishDraftResponse
  }

  async function unpublishPublication(newsId: number): Promise<HttpResult<api.News> | undefined> {
    if (projectSlugOrId === undefined) {
      return
    }

    const unpublishPublicationResponse = await base.unpublishPublication(newsId)

    unpublishPublicationResponse.next(() => {
      const newCache = Object.fromEntries(
        Object.entries(sharedQuery.cache).filter(([key]) => {
          return !key.startsWith(String(projectSlugOrId))
        }),
      )

      setProjectNewsCache(newCache)
      query()
    })

    return unpublishPublicationResponse
  }

  async function updatePublication(
    newsId: number,
    payload: api.CreateNewsDraft,
  ): Promise<HttpResult<api.News> | undefined> {
    if (projectSlugOrId === undefined) {
      return
    }

    const updatePublicationResponse = await base.updatePublication(newsId, payload)

    updatePublicationResponse.next(() => {
      const newCache = Object.fromEntries(
        Object.entries(sharedQuery.cache).filter(([key]) => {
          return !key.startsWith(String(projectSlugOrId))
        }),
      )

      setProjectNewsCache(newCache)
      query()
    })

    return updatePublicationResponse
  }

  async function deletePublication(newsId: number): Promise<HttpResult<void> | undefined> {
    if (projectSlugOrId === undefined) {
      return
    }

    const deletePublicationResponse = await base.deletePublication(newsId)

    deletePublicationResponse.next(() => {
      const newCache = Object.fromEntries(
        Object.entries(sharedQuery.cache).filter(([key]) => {
          return !key.startsWith(String(projectSlugOrId))
        }),
      )

      setProjectNewsCache(newCache)
      query()
    })

    return deletePublicationResponse
  }
}

function id(projectId: number | string | undefined, params: UseProjectNewsParams, withToken: boolean): string {
  return `${String(projectId)}:${JSON.stringify([
    params.limit,
    params.page,
    params.status,
    params.sort,
    params.q,
    withToken,
  ])}`
}
